import React, { useEffect, useMemo, useRef } from "react";
import { X, History, FileText, Check, Clock } from "lucide-react";
import { createBulkStyles, BULK_CSS } from "./BulkActions/bulkActions.styles";
import { Btn } from "./BulkActions/BulkSteps";
import EmployeeSummaryCard from "./EmployeeSummaryCard";

const STATUS_COLORS = {
  approved: "#10b981",
  completed: "#10b981",
  pending: "#f59e0b",
  scheduled: "#3b82f6",
  rejected: "#ef4444",
  cancelled: "#94a3b8",
};

/**
 * One employee's action history — same shell as the Employee Action modal:
 * the employee card (+ a status summary) in the side panel and a timeline of
 * every action taken on them beside it.
 */
const EmployeeHistoryModal = ({
  t,
  theme,
  isRTL,
  language,
  employee,
  historyData,
  getEmployeeName,
  ACTION_TYPES,
  onClose,
  // Manager view: no salary numbers (the server doesn't send them either).
  hideSalary = false,
}) => {
  const s = useMemo(() => createBulkStyles(theme, isRTL), [theme, isRTL]);
  const k = s.k;
  const lang = language === "ar" ? "ar" : "en";
  const shellRef = useRef(null);
  const list = useMemo(() => (Array.isArray(historyData) ? historyData : []), [historyData]);
  const name = getEmployeeName(employee);

  useEffect(() => {
    shellRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const locale = lang === "ar" ? "ar-EG-u-nu-latn" : "en-GB";
  const fmtDate = (v) => {
    if (!v) return t.notAvailable;
    const d = new Date(v);
    return Number.isNaN(d.getTime())
      ? String(v)
      : new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric" }).format(d);
  };
  const fmtNum = (v) => Number(v).toLocaleString(locale, { maximumFractionDigits: 2 });
  const arrow = isRTL ? "←" : "→";
  const statusKey = (a) => String(a.status || "").toLowerCase();

  const counts = useMemo(() => {
    const c = {};
    list.forEach((a) => {
      const key = statusKey(a);
      if (key) c[key] = (c[key] || 0) + 1;
    });
    return c;
  }, [list]);

  const statusPill = (a) => {
    const key = statusKey(a);
    const color = STATUS_COLORS[key] || "#94a3b8";
    const Icon = key === "approved" || key === "completed" ? Check : key === "pending" ? Clock : key ? X : null;
    return (
      <span style={s.pill(color)}>
        {Icon && <Icon size={12} strokeWidth={3} />}
        {t[key] || a.status || t.unknown}
      </span>
    );
  };

  const renderItem = (a, index) => {
    const cfg = ACTION_TYPES.find((x) => x.id === a.actionType);
    const Icon = cfg?.icon || FileText;
    const changes = [
      [t.position, a.oldPosition ?? a.oldData?.position, a.newPosition ?? a.newData?.position],
      [t.department, a.oldDepartment ?? a.oldData?.department, a.newDepartment ?? a.newData?.department],
      [t.directManager, a.oldReportsTo ?? a.oldData?.reportsTo, a.newReportsTo ?? a.newData?.reportsTo],
      ...(!hideSalary
        ? [[lang === "ar" ? "صافي الراتب" : "Net salary", a.oldSalary ?? a.oldData?.salary, a.newSalary ?? a.newData?.salary, true]]
        : []),
    ].filter(([, from, to]) => to !== null && to !== undefined && to !== "" && String(to) !== String(from ?? ""));
    const last = index === list.length - 1;

    return (
      <li key={a.id} style={{ display: "flex", gap: 14, position: "relative", paddingBottom: last ? 0 : 14 }}>
        {/* rail */}
        {!last && <span style={{ position: "absolute", insetInlineStart: 19, top: 42, bottom: 0, width: 2, background: k.border }} />}
        <div style={{ ...s.actionIcon(cfg?.color || "#64748b"), width: 40, height: 40, borderRadius: 12, marginBottom: 0, flexShrink: 0 }}>
          <Icon size={19} />
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            padding: "12px 16px",
            borderRadius: 14,
            border: `1px solid ${k.border}`,
            background: k.dark ? "rgba(255,255,255,0.03)" : "#ffffff",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 15.5, fontWeight: 800 }}>{cfg ? t[cfg.labelKey] : a.actionType}</span>
              {statusPill(a)}
            </div>
            <span style={{ fontSize: 13, color: k.muted, fontWeight: 600, whiteSpace: "nowrap" }}>
              {fmtDate(a.effectiveDate || a.createdAt)}
            </span>
          </div>

          {changes.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 22px", marginTop: 10 }}>
              {changes.map(([label, from, to, money]) => {
                const up = money && from != null ? Number(to) >= Number(from) : undefined;
                return (
                  <div key={label} style={{ fontSize: 13.5 }}>
                    <span style={{ color: k.muted }}>{label}: </span>
                    <bdi style={{ color: k.muted }}>{from != null && from !== "" ? (money ? fmtNum(from) : from) : "—"}</bdi>
                    {` ${arrow} `}
                    <bdi style={{ fontWeight: 700, color: up === undefined ? k.text : up ? "#10b981" : "#ef4444" }}>
                      {money ? `${fmtNum(to)} ${t.egp}` : to}
                    </bdi>
                    {money && a.changePercent != null && Number(a.changePercent) !== 0 && (
                      <span style={{ color: up ? "#10b981" : "#ef4444", fontSize: 12.5, fontWeight: 700 }}>
                        {" "}<bdi dir="ltr">({Number(a.changePercent) > 0 ? "+" : ""}{Number(a.changePercent).toFixed(1)}%)</bdi>
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {a.reason && (
            <div style={{ marginTop: 10, fontSize: 13.5, lineHeight: 1.55 }}>
              <span style={{ color: k.muted }}>{t.reason}: </span>
              <bdi style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{a.reason}</bdi>
            </div>
          )}
          {a.rejectionReason && (
            <div style={{ marginTop: 6, fontSize: 13.5, lineHeight: 1.55, color: "#ef4444" }}>
              <span>{t.rejectionReason}: </span>
              <bdi style={{ wordBreak: "break-word" }}>{a.rejectionReason}</bdi>
            </div>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className="nxb-overlay" style={{ ...s.overlay, zIndex: 1000 }} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <style>{BULK_CSS}</style>
      <style>{`
        .nx-eh-body { flex: 1; min-height: 0; display: flex; }
        .nx-eh-aside {
          width: 340px; flex-shrink: 0; box-sizing: border-box; overflow-y: auto; padding: 14px;
          background: ${k.subtle}; border-inline-end: 1px solid ${k.border};
        }
        .nx-eh-main { flex: 1; min-width: 0; overflow-y: auto; padding: 18px 24px; }
        @media (max-width: 900px) {
          .nx-eh-body { flex-direction: column; overflow-y: auto; }
          .nx-eh-aside { width: auto; overflow: visible; border-inline-end: none; border-bottom: 1px solid ${k.border}; }
          .nx-eh-main { overflow: visible; }
        }
      `}</style>
      <div
        ref={shellRef}
        className="nxb-shell"
        role="dialog"
        aria-modal="true"
        aria-labelledby="nx-eh-title"
        tabIndex={-1}
        style={{ ...s.shell, width: "min(1180px, 100%)", height: "min(820px, 95vh)", position: "relative", outline: "none" }}
      >
        <div className="nxb-header" style={{ ...s.header, padding: "12px 28px", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "center", minWidth: 0 }}>
            <div style={{ ...s.headerIcon, width: 46, height: 46, borderRadius: 13 }}><History size={22} /></div>
            <div style={{ minWidth: 0 }}>
              <h2 id="nx-eh-title" style={{ margin: 0, fontSize: 21, fontWeight: 800, letterSpacing: "-0.01em" }}>{t.employeeHistoryTitle}</h2>
              <p style={{ margin: "2px 0 0", fontSize: 14, color: k.muted }}>
                {list.length} {t.records}
              </p>
            </div>
          </div>
          <button type="button" className="nxb-btn" style={s.iconButton} aria-label={t.close} title={t.close} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="nx-eh-body">
          <aside className="nx-eh-aside">
            <EmployeeSummaryCard employee={employee} name={name} t={t} s={s} language={language} hideSalary={hideSalary} />

            {list.length > 0 && (
              <div style={{ marginTop: 8, padding: 12, borderRadius: 14, border: `1px solid ${k.border}`, background: k.dark ? "rgba(255,255,255,0.03)" : "#ffffff" }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: k.muted, marginBottom: 8 }}>{t.actionTimeline}</div>
                <div style={{ display: "grid", gap: 6 }}>
                  {["approved", "completed", "pending", "scheduled", "rejected", "cancelled"]
                    .filter((st) => counts[st])
                    .map((st) => (
                      <div key={st} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 14 }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <span style={s.dot(STATUS_COLORS[st])} />
                          {t[st] || st}
                        </span>
                        <strong>{counts[st]}</strong>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </aside>

          <div className="nx-eh-main">
            {list.length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 12px" }}>
                <div style={s.dropIcon}><History size={28} /></div>
                <h3 style={{ margin: "0 0 6px", fontSize: 19, fontWeight: 800 }}>{t.noHistoryFound}</h3>
                <p style={{ margin: 0, fontSize: 14, color: k.muted }}>{t.noActionsForEmployee}</p>
              </div>
            ) : (
              <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>{list.map(renderItem)}</ol>
            )}
          </div>
        </div>

        <div className="nxb-footer" style={{ ...s.footer, padding: "12px 28px" }}>
          <span />
          <Btn s={s} variant="secondary" onClick={onClose}>{t.close}</Btn>
        </div>
      </div>
    </div>
  );
};

export default EmployeeHistoryModal;
